import { CallImportance, TechLevel, InvestigativeValue } from './types';
import type { VisibilityPresetName, VisibilityPreferences } from './types';

type PresetThresholds = Omit<VisibilityPreferences, 'overrides' | 'activePreset'>;

export const VISIBILITY_PRESETS: Record<VisibilityPresetName, PresetThresholds> = {
  /** New / basic user — only critical + high importance, basic tech level */
  essentials: {
    importanceThreshold: CallImportance.HIGH,
    techLevelThreshold: TechLevel.BASIC,
    investigativeThreshold: InvestigativeValue.NONE,
  },
  /** Trained operator — adds medium importance, intermediate tech */
  standard: {
    importanceThreshold: CallImportance.MEDIUM,
    techLevelThreshold: TechLevel.INTERMEDIATE,
    investigativeThreshold: InvestigativeValue.NONE,
  },
  /** Experienced investigator — everything except dev items */
  advanced: {
    importanceThreshold: CallImportance.LOW,
    techLevelThreshold: TechLevel.EXPERT,
    investigativeThreshold: InvestigativeValue.NONE,
  },
  /** Power user / admin — everything except dev */
  full: {
    importanceThreshold: CallImportance.LOW,
    techLevelThreshold: TechLevel.EXPERT,
    investigativeThreshold: InvestigativeValue.NONE,
  },
  /** Supervised learning — everything + training-specific items */
  training: {
    importanceThreshold: CallImportance.DEV,
    techLevelThreshold: TechLevel.EXPERT,
    investigativeThreshold: InvestigativeValue.NONE,
  },
  /** Developer — absolutely everything visible */
  developer: {
    importanceThreshold: CallImportance.DEV,
    techLevelThreshold: TechLevel.DEV,
    investigativeThreshold: InvestigativeValue.NONE,
  },
  /** Custom — placeholder, real values come from user's slider positions */
  custom: {
    importanceThreshold: CallImportance.LOW,
    techLevelThreshold: TechLevel.EXPERT,
    investigativeThreshold: InvestigativeValue.NONE,
  },
};

export const PRESET_META: Record<VisibilityPresetName, { label: string; description: string }> = {
  essentials:  { label: 'Essentials',  description: 'Core call controls only' },
  standard:    { label: 'Standard',    description: 'Recommended for most users' },
  advanced:    { label: 'Advanced',    description: 'Full investigative tools' },
  full:        { label: 'Full',        description: 'Everything except dev tools' },
  training:    { label: 'Training',    description: 'Includes training features' },
  developer:   { label: 'Developer',   description: 'All elements including debug' },
  custom:      { label: 'Custom',      description: 'Manually adjusted sliders' },
};

// ── UI-simplification master switch ──────────────────────────────────────────
// Product decision (2026-06-05): the per-tier "simplify the UI" feature was hiding
// genuinely useful controls from real users (header auto-respond toggle, control-bar
// device menus, transcript pin, action-card lock/deprioritize/follow-up/remove/
// feedback, and at times whole tabs). EVERYONE should get the full UI.
//
// When FALSE, the visibility system is a hard no-op: `computeHiddenIds` returns an
// empty set, so NOTHING is ever hidden — regardless of preset, role, persisted prefs,
// or per-component overrides. The presets/registry/settings UI remain in the codebase
// (so the feature can be re-enabled by flipping this to `true`) but have no effect.
export const VISIBILITY_SIMPLIFICATION_ENABLED = false;

// Default to showing everything: the tiered presets were hiding genuinely useful
// controls (action-card lock/deprioritize/follow-up/remove/feedback, etc.). 'full'
// reveals every real feature and excludes only the 4 raw dev/debug AI-payload tabs.
// (Only consulted when VISIBILITY_SIMPLIFICATION_ENABLED is true.)
export const DEFAULT_PRESET: VisibilityPresetName = 'full';

export const DEFAULT_PREFERENCES: VisibilityPreferences = {
  activePreset: DEFAULT_PRESET,
  ...VISIBILITY_PRESETS[DEFAULT_PRESET],
  overrides: {},
};
